import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { notFound } from '@tanstack/react-router';
import { api, PaginationResponse } from '@/api';
import type { WorkspaceListItem, Workspace } from '@/features/workspaces/types';
import { mapWorkspaceType, resourceTypeToLabel } from '@/features/workspaces/utils';
import { DOCUMENT_TYPE } from '@/features/documents/constants';
import {
  ANALYSIS_TERMINAL_STATUSES,
  AnalysisStatus,
  type WorkspaceType,
} from '@/features/workspaces/constants';
import { isEmptySnapshotRow, queryStream } from '@/features/workspaces/stream';

export const WORKSPACES_QUERY_KEY = 'workspaces';

export const workspacesQuery = ({
  organizationId,
  projectId,
  resourceType,
  pageSize = 20,
}: {
  organizationId: string;
  projectId: string;
  resourceType?: number;
  pageSize?: number;
}) =>
  infiniteQueryOptions({
    queryKey: [WORKSPACES_QUERY_KEY, organizationId, projectId, 'infinite', resourceType, pageSize],
    queryFn: async ({ pageParam }) => {
      if (!organizationId) return { data: [], pageCount: 0, totalCount: 0, pageSize: 0 };
      const { data } = await api.get<PaginationResponse<WorkspaceListItem>>('v1/evaluations', {
        params: {
          organizationId,
          projectId,
          resourceType,
          page: pageParam,
          pageSize,
          includeMetrics: true,
        },
      });
      return { ...data, data: data.data.map(mapWorkspaceType) };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      allPages.length < lastPage.pageCount ? allPages.length + 1 : undefined,
  });

export const organizationWorkspacesQuery = (organizationId: string) =>
  queryOptions({
    queryKey: ['organizationWorkspaces', organizationId],
    queryFn: async () =>
      (
        await api.get<
          { id: string; name: string; type: number; projectId: string; projectName: string }[]
        >('v1/evaluations/lite', { params: { organizationId } })
      ).data.map((item) => ({
        ...item,
        type: resourceTypeToLabel(item.type) ?? (item.type as unknown as WorkspaceType),
      })),
  });

export const projectWorkspacesQuery = (projectId: string) =>
  queryOptions({
    queryKey: ['projectWorkspaces', projectId],
    queryFn: async () =>
      (
        await api.get<{ id: string; name: string; type: number }[]>(
          `v1/projects/${projectId}/evaluations`,
        )
      ).data.map((item) => ({
        ...item,
        type: resourceTypeToLabel(item.type) ?? (item.type as unknown as WorkspaceType),
      })),
  });

export const workspaceQuery = (workspaceId: string) => {
  const queryKey = ['workspace', workspaceId];
  return queryOptions({
    staleTime: 600 * 1000,
    queryKey,
    queryFn: async () => {
      const { data } = await api.get<Omit<Workspace, 'type'> & { resourceType: number }>(
        `v1/evaluations/${workspaceId}`,
      );
      const type = resourceTypeToLabel(data.resourceType);
      if (!type) throw notFound();
      return { ...data, type };
    },
    meta: {
      stream: queryStream<Workspace, { id?: string | null; status: AnalysisStatus | null }>({
        topic: 'tender_criteria',
        type: DOCUMENT_TYPE.tenderPackage,
        resourceId: workspaceId,
        isEnabled: (data) => data?.type === 'tenderPackage',
        isActive: (data) =>
          !!data &&
          !ANALYSIS_TERMINAL_STATUSES.includes(
            data.tenderCriteriaStatus ?? AnalysisStatus.NOT_STARTED,
          ),
        onReconnect: (queryClient) => queryClient.invalidateQueries({ queryKey }),
        onStatusUpdate: (row, queryClient) => {
          const current = queryClient.getQueryData<Workspace>(queryKey);
          if (isEmptySnapshotRow(row)) {
            const status = current?.tenderCriteria?.length
              ? AnalysisStatus.DONE
              : AnalysisStatus.NOT_STARTED;
            if (current && (current.tenderCriteriaStatus ?? AnalysisStatus.NOT_STARTED) === status)
              return;
            return queryClient.invalidateQueries({ queryKey });
          }

          const status = row.status;
          if (status == null || status === current?.tenderCriteriaStatus) return;
          queryClient.setQueryData<Workspace>(
            queryKey,
            (old) => old && { ...old, tenderCriteriaStatus: status },
          );
          if (ANALYSIS_TERMINAL_STATUSES.includes(status))
            return queryClient.invalidateQueries({ queryKey });
        },
      }),
    },
  });
};

export const resourceVersionsQuery = (resourceId: string) =>
  queryOptions({
    queryKey: ['resourceVersions', resourceId],
    queryFn: async () =>
      (
        await api.get<{
          versions: {
            version: {
              id: string;
              versionNumber: number;
              createdBy: string | null;
              createdAt: string | null;
            };
            diff: { addedDocIds: string[]; removedDocIds: string[] };
          }[];
          documentsMetadata: Record<string, { documentFilename: string | null }>;
        }>(`v1/versioning/evaluations/${resourceId}/versions`, { params: { include_diff: true } })
      ).data,
  });

export const resourceLatestVersionQuery = (resourceId: string) =>
  queryOptions({
    queryKey: ['resourceLatestVersion', resourceId],
    queryFn: async () => {
      const { data, status } = await api.get<{
        version: { id: string; versionNumber: number; status: string };
      }>(`v1/versioning/evaluations/${resourceId}/versions/latest`, {
        validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
      });
      if (status === 404) return null;
      return data;
    },
  });
